import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useRef, useState } from 'react'

import type {
  CellContextMenuEvent,
  GetContextMenuItemsParams,
  GetRowIdParams,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideSelectionState,
  MenuItemDef,
  ModelUpdatedEvent,
  RowClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement, RefObject, SyntheticEvent } from 'react'
import type { IContext } from '~/components/CellRenderers/ClusterApplicationCellRenderer'
import type { IClusterApp, IGetClusterCandidatesParams } from '~/types/cluster'

import columns, { COLUMNS } from './columns'

import ClusterFilters from '../Filters'

import { getClusterCandidates } from '~/api/clusters'
import Button from '~/components/Buttons/Button'
import ServerSideTableWrapper from '~/components/Tables/ServerSideTableWrapper'
import { useClustersContext } from '~/providers/Clusters'
import { keys as clustersKeys, useBatchAddToClusterMutation } from '~/queries/clusters'
import { debounce } from '~/utils/function'
import { displayToast } from '~/utils/toast'

const CACHE_BLOCK_SIZE = 50

const ENTRIES_NAME = 'competitors-cluster-details-candidates'

interface Props {
  clusterId: string
  getContextMenuItems: (params: GetContextMenuItemsParams) => MenuItemDef[]
  gridRef: RefObject<AgGridReact<IClusterApp>> | null
  onCellContextMenu: (event: CellContextMenuEvent) => void
  onContactIconClick: (email: string, phone: string) => void
  onDisplayScreenshots: (app: IClusterApp) => void
  onRowSelectionChanged: (selectedRows: IClusterApp[]) => void
}

const ExploreTable = ({
  clusterId,
  getContextMenuItems,
  gridRef,
  onCellContextMenu,
  onContactIconClick,
  onDisplayScreenshots,
  onRowSelectionChanged,
}: Props): ReactElement => {
  /** Local state */

  const { dispatch: dispatchToClustersContext, state } = useClustersContext()

  const queryClient = useQueryClient()

  const tableRef = useRef<HTMLDivElement>(null)

  const [search, setSearch] = useState('')

  const [selectedRows, setSelectedRows] = useState<IClusterApp[]>([])

  const debouncedSearch = debounce((search) => setSearch(search), 500)

  const datasource: IServerSideDatasource = useMemo(
    () => ({
      getRows: async (params: IServerSideGetRowsParams) => {
        const { api, fail, request, success } = params

        api.hideOverlay()

        const currentPage = (request.startRow || 0) / CACHE_BLOCK_SIZE

        const opFiltering = request.filterModel
          ? Object.entries(request.filterModel)
              .map(
                ([key, { filter, filterTo, type }]) =>
                  `${type}(${key}, ${type === 'inRange' ? `${filter}, ${filterTo}` : `${filter}`})`
              )
              .join('+')
          : undefined

        const queryParams: IGetClusterCandidatesParams = {
          deviceType: state.deviceType,
          opFiltering,
          orderBy: request.sortModel.length ? `${request.sortModel[0].colId}+${request.sortModel[0].sort}` : undefined,
          page: currentPage,
          q: search,
          searchElements: state.searchElements,
          size: CACHE_BLOCK_SIZE,
        }

        const { data, total_filtered } = await queryClient.fetchQuery({
          queryFn: () => getClusterCandidates(clusterId, queryParams),
          queryKey: clustersKeys.clusterCandidates(clusterId, queryParams),
        })

        dispatchToClustersContext({ type: 'nbVisibleApps', value: total_filtered })

        if (data) {
          if (!data.length) {
            // NOTE: dispatchToClustersContext is re-rendering the view hidding the overlay,
            // setTimeout is a workaround to display the overlay after the state update
            setTimeout(() => api.showNoRowsOverlay(), 50)
          }
          success({ rowData: data })
        } else {
          fail()
        }
      },
    }),
    [clusterId, dispatchToClustersContext, queryClient, search, state.deviceType, state.searchElements]
  )

  /** Queries & Mutations */

  const { mutateAsync: batchAddToCluster } = useBatchAddToClusterMutation()

  /** Handlers */

  const handleGetRowId = useCallback(({ data }: GetRowIdParams<IClusterApp>) => data.store_id, [])

  const handleRowClicked = useCallback(
    ({ node }: RowClickedEvent<IClusterApp>) => {
      const colId = gridRef?.current?.api.getFocusedCell()?.column.getColId()

      if (colId === COLUMNS.SCREENSHOTS && node.data) {
        onDisplayScreenshots(node.data)
      }
    },
    [gridRef, onDisplayScreenshots]
  )

  const handleModelUpdated = useCallback(
    (params: ModelUpdatedEvent) => {
      dispatchToClustersContext({ type: 'nbVisibleApps', value: params.api.getDisplayedRowCount() })
    },
    [dispatchToClustersContext]
  )

  const handleRowSelectionChanged = useCallback(
    (evt: SelectionChangedEvent<IClusterApp>) => {
      const state = evt.api.getServerSideSelectionState() as IServerSideSelectionState | null
      const newSelectedRows: IClusterApp[] = []
      if (state && !state.selectAll && state.toggledNodes?.length) {
        evt.api.forEachNode(
          (node) => node.data && state.toggledNodes.includes(node.data.store_id) && newSelectedRows.push(node.data)
        )
      } else if (state && state.selectAll) {
        evt.api.forEachNode((node) => node.data && newSelectedRows.push(node.data))
      }

      setSelectedRows(newSelectedRows)
      onRowSelectionChanged(newSelectedRows)
    },
    [onRowSelectionChanged]
  )

  const handleSearchChange = (e: SyntheticEvent<HTMLInputElement>) => debouncedSearch(e.currentTarget.value)

  const handleAddToCluster = async () => {
    if (clusterId && selectedRows && selectedRows.length > 0) {
      try {
        const itemsToAdd = selectedRows.map(({ device_type, store_id }) => ({
          deviceType: device_type,
          isTarget: false,
          storeId: store_id,
        }))
        await batchAddToCluster({
          clusterId,
          items: itemsToAdd,
        })
        await queryClient.invalidateQueries({
          queryKey: clustersKeys.listAppsForClusters({
            clusterId,
            deviceType: state.deviceType,
            filterPreset: state.filterPreset,
            tags: state.tags,
          }),
        })
        await queryClient.invalidateQueries({ queryKey: clustersKeys.details(clusterId) })
        await queryClient.invalidateQueries({ queryKey: clustersKeys.clusterStats(clusterId) })
        await queryClient.invalidateQueries({
          queryKey: clustersKeys.clusterCandidates(clusterId, {
            deviceType: state.deviceType,
            searchElements: state.searchElements,
          }),
        })
        displayToast('App(s) successfully added to cluster', 'success')
      } catch (e) {
        captureException(e)
        displayToast('App(s) could not be added to cluster', 'error')
      }
    }
  }

  /** Render */

  const context: IContext = {
    onContactIconClick,
  }

  return (
    <ServerSideTableWrapper
      buttonComponent={
        <div className="cluster-details-view__table__actions">
          <Button disabled={!selectedRows.length} onClick={handleAddToCluster} size="small">
            Add to cluster
          </Button>
        </div>
      }
      cacheBlockSize={CACHE_BLOCK_SIZE}
      className="cluster-details-view__table cluster-details-view__explore-table"
      columnDefs={columns}
      context={context}
      defaultColDef={{
        filterParams: { buttons: ['clear'], debounceMs: 500 },
        getQuickFilterText: () => '',
        menuTabs: ['filterMenuTab'],
      }}
      entriesName={ENTRIES_NAME}
      getContextMenuItems={getContextMenuItems}
      getRowId={handleGetRowId}
      isSideBar
      nodeBeforeSearchBar={<ClusterFilters />}
      onCellContextMenu={onCellContextMenu}
      onModelUpdated={handleModelUpdated}
      onRowClicked={handleRowClicked}
      onSearchChange={handleSearchChange}
      onSelectionChanged={handleRowSelectionChanged}
      popupParent={document.querySelector('body')}
      preventDefaultOnContextMenu
      ref={gridRef}
      rowHeight={250}
      rowModelType="serverSide"
      rowSelection={{
        enableClickSelection: true,
        enableSelectionWithoutKeys: true,
        headerCheckbox: false,
        mode: 'multiRow',
      }}
      serverSideDatasource={datasource}
      suppressCsvExport
      suppressExcelExport
      tableRef={tableRef}
      withPagination={false}
      withSearchBar
    />
  )
}

export default ExploreTable
